import { useEffect, useRef, useState } from "react";
import { doc, onSnapshot, setDoc, deleteDoc } from "firebase/firestore";
import { db, ensureSignedIn } from "./firebase";
import ParallaxBackground from "./sections/ParallaxBackground";
import Intro from "./sections/Intro";
import Questionnaire from "./sections/Questionnaire";
import HerQuestions from "./sections/HerQuestions";
import MiniGame from "./sections/MiniGame";
import DateAsk from "./sections/DateAsk";
import Finale from "./sections/Finale";
import useUiSounds from "./useUiSounds";
import "./styles.css";

const SESSION_ID = "dua";
const RESET_PASSWORD = "shooters";
const NUM_QUESTIONS = 9;
const DEFAULT_SESSION = {
  scene: "intro",
  qIndex: 0,
  answers: {},
  submitted: {},
  herQuestions: ["", "", ""],
};

// 14 stops total: intro, q1..q9, herQuestions, miniGame, dateAsk, finale.
function calcStep(session) {
  switch (session.scene) {
    case "intro":
      return 0;
    case "questionnaire":
      return 1 + Math.min(session.qIndex || 0, NUM_QUESTIONS - 1);
    case "herQuestions":
      return 1 + NUM_QUESTIONS;
    case "miniGame":
      return 2 + NUM_QUESTIONS;
    case "dateAsk":
      return 3 + NUM_QUESTIONS;
    case "finale":
      return 4 + NUM_QUESTIONS;
    default:
      return 0;
  }
}

function TopProgressBar({ session }) {
  const total = 4 + NUM_QUESTIONS;
  const pct = (calcStep(session) / total) * 100;
  return (
    <div className="top-progress" aria-hidden="true">
      <div className="top-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(DEFAULT_SESSION);
  const remoteOkRef = useRef(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetting, setResetting] = useState(false);

  useUiSounds();

  useEffect(() => {
    let cancelled = false;
    let unsub = null;

    ensureSignedIn()
      .then(() => {
        if (cancelled) return;
        const ref = doc(db, "sessions", SESSION_ID);
        unsub = onSnapshot(
          ref,
          (snap) => {
            remoteOkRef.current = true;
            if (snap.exists()) setSession({ ...DEFAULT_SESSION, ...snap.data() });
          },
          (err) => {
            console.warn(
              "[firestore disabled — local-only mode]",
              err.code || err.message
            );
            remoteOkRef.current = false;
          }
        );
      })
      .catch((e) => {
        console.warn("[auth disabled — local-only mode]", e.message);
      });

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  // Scroll to top on every scene change or question change.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [session.scene, session.qIndex]);

  const update = async (patch) => {
    setSession((prev) => ({ ...prev, ...patch }));
    if (!remoteOkRef.current) return;
    try {
      const ref = doc(db, "sessions", SESSION_ID);
      await setDoc(ref, patch, { merge: true });
    } catch (e) {
      console.warn("[firestore write failed]", e.message);
    }
  };

  const goTo = (scene) => update({ scene });

  const openReset = () => {
    setResetPw("");
    setResetError("");
    setResetOpen(true);
  };

  const cancelReset = () => {
    if (resetting) return;
    setResetOpen(false);
    setResetPw("");
    setResetError("");
  };

  const confirmReset = async () => {
    if (resetting) return;
    if (resetPw !== RESET_PASSWORD) {
      setResetError("nope.");
      return;
    }
    setResetting(true);
    setResetError("");
    setSession(DEFAULT_SESSION);
    if (remoteOkRef.current) {
      try {
        await deleteDoc(doc(db, "sessions", SESSION_ID));
      } catch (e) {
        console.warn("[firestore delete failed]", e.message);
      }
    }
    window.location.reload();
  };

  const sceneProps = { session, update, goTo, sessionId: SESSION_ID };

  return (
    <div className="App">
      <ParallaxBackground />

      <TopProgressBar session={session} />

      <main className="page">
        {session.scene === "intro" && <Intro {...sceneProps} />}
        {session.scene === "questionnaire" && <Questionnaire {...sceneProps} />}
        {session.scene === "herQuestions" && <HerQuestions {...sceneProps} />}
        {session.scene === "miniGame" && <MiniGame {...sceneProps} />}
        {session.scene === "dateAsk" && <DateAsk {...sceneProps} />}
        {session.scene === "finale" && <Finale {...sceneProps} />}
      </main>

      <button
        onClick={openReset}
        data-no-sfx
        title="reset"
        aria-label="reset"
        style={{
          position: "fixed",
          bottom: 8,
          right: 8,
          width: 14,
          height: 14,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          opacity: 0.15,
          padding: 0,
          fontSize: 10,
          color: "var(--muted)",
          zIndex: 100,
        }}
      >
        ·
      </button>

      {resetOpen && (
        <div
          className="reset-modal-backdrop"
          onClick={cancelReset}
          role="presentation"
        >
          <div
            className="reset-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <p className="reset-modal-title">reset everything?</p>
            <p className="reset-modal-sub">
              this wipes ALL answers and starts over.
            </p>
            <input
              type="password"
              autoFocus
              placeholder="password"
              value={resetPw}
              onChange={(e) => {
                setResetPw(e.target.value);
                if (resetError) setResetError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmReset();
                if (e.key === "Escape") cancelReset();
              }}
              disabled={resetting}
            />
            {resetError && <p className="reset-modal-err">{resetError}</p>}
            <div className="reset-modal-actions">
              <button
                className="btn btn-ghost"
                onClick={cancelReset}
                disabled={resetting}
              >
                <span className="btn-text">cancel</span>
              </button>
              <button
                className="btn"
                onClick={confirmReset}
                disabled={resetting || !resetPw}
              >
                <span className="btn-text">
                  {resetting ? "wiping…" : "wipe"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
